import React, {useState} from 'react';
import {useTranslation} from "react-i18next";
import {LocalDocumentCategory} from "../../../../../models/local-documents.models";

import {ActionButton, CancelButton} from "../../../../../components/common/ActionButtons";
import SecondaryActionButton from "../../../../../components/documents-digitization/SecondaryActionButton";
import DigitizedDocumentType from "../../../../../components/documents-digitization/DigitizedDocumentType";

import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import {StyledBoxWrapper} from "../../DigitizationDetails.styled";
import {NoCategoryIconBox} from "./DigitizationDocumentCategory.styled";
import {tokens} from "../../../../../theme";
import {semanticColors} from "../../../../../theme/tokens/tokens";

interface DigitizationDocumentCategoryProps {
    category: LocalDocumentCategory | null,
    onChange: (category: LocalDocumentCategory) => Promise<void>;
}

const DigitizationDocumentCategory = ({category, onChange}: DigitizationDocumentCategoryProps) => {
    const [openDialog, setOpenDialog] = useState(false);
    const {t} = useTranslation();

    return (
        <StyledBoxWrapper>
            <Typography color="text.secondary" fontWeight={500} variant={"body2"} sx={{textTransform: "uppercase"}}>
                {t('digitizationDetails.category.title')}
            </Typography>

            {(!category || category === 'NOTYPE') && (
                <>
                    <Stack direction="row" alignItems="center" justifyContent="left" height="100%">
                        <NoCategoryIconBox>
                            <AccessTimeRoundedIcon />
                        </NoCategoryIconBox>

                        <Typography variant={"body1"} color={semanticColors.orange.light.fg} fontWeight={tokens.fontWeight.medium} ml={2}>
                            {t('digitizationDetails.category.unknown')}
                        </Typography>
                    </Stack>

                    <SecondaryActionButton onClick={() => setOpenDialog(true)}>
                        {t('digitizationDetails.category.chooseCategoryButton')}
                    </SecondaryActionButton>
                </>
            )}

            {(category && category !== 'NOTYPE') && (
                <>
                    <Box display="flex" alignItems="center" gap={1} mt={2} mb={2}>
                        <TextSnippetOutlinedIcon sx={{ fontSize: '24px', color: "text.secondary" }} />
                        <DigitizedDocumentType type={category} />
                    </Box>

                    <Button onClick={() => setOpenDialog(true)} sx={{ flexShrink: 0, alignSelf: 'flex-start' }}>
                        {t('digitizationDetails.category.changeButton')}
                    </Button>
                </>
            )}


            {openDialog && <ChangeDocumentCategoryDialog open={openDialog} onChange={onChange} onClose={() => setOpenDialog(false)}/>}
        </StyledBoxWrapper>
    );
};

interface ChangeDocumentCategoryDialogProps {
    open: boolean;
    onChange: (category: LocalDocumentCategory) => Promise<void>;
    onClose: () => void;
}

const ChangeDocumentCategoryDialog = ({open, onChange, onClose }: ChangeDocumentCategoryDialogProps) => {
    const {t} = useTranslation();

    const DocumentCategoryLabels: Partial<Record<LocalDocumentCategory, string>> = {
        MEDICAL_HISTORY: t('digitizationDetails.documentType.MEDICAL_HISTORY') as string,
        TEST_RESULTS: t('digitizationDetails.documentType.TEST_RESULTS') as string,
        REFERRAL: t('digitizationDetails.documentType.REFERRAL') as string,
        STATEMENT: t('digitizationDetails.documentType.STATEMENT') as string,
        OTHER: t('digitizationDetails.documentType.OTHER') as string,
    };

    const options = Object.entries(DocumentCategoryLabels).map(([value, label]) => ({
        value: value as LocalDocumentCategory,
        label,
    }));

    const [selected, setSelected] = useState<string | null>(null);

    const handleSelectionChange = async () => {
        await onChange(selected as LocalDocumentCategory);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            sx={{ overflow: 'auto', height: '100%'}}
            aria-labelledby="change-document-category-dialog-title"
            aria-modal="true"
        >
            <DialogTitle
                id="change-document-category-dialog-title"
                sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}
            >
                {t('digitizationDetails.category.chooseCategoryTitle')}

                <Tooltip title={t('common.close')}>
                    <IconButton onClick={onClose}
                                size="small"
                                aria-label={t('common.close') as string}>
                        <CloseIcon />
                    </IconButton>
                </Tooltip>
            </DialogTitle>

            <DialogContent>
                <List disablePadding>
                    {options.map((option) => (
                        <ListItemButton
                            key={option.value}
                            selected={selected === option.value}
                            onClick={() => setSelected(option.value)}
                            sx={{ borderRadius: tokens.radius.md }}
                        >
                            <ListItemText primary={option.label} />
                        </ListItemButton>
                    ))}
                </List>
            </DialogContent>

            <DialogActions >
                <CancelButton onClick={onClose}>
                    {t('common.cancel')}
                </CancelButton>

                <ActionButton
                    onClick={handleSelectionChange}
                    disabled={!selected}
                >
                    {t('common.save')}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default DigitizationDocumentCategory;